import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  ShieldAlert, ShieldCheck, Search, Download, Trash2, 
  FileText, Calendar, User, Clock, AlertTriangle, Play, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = '';
if (!ENV_BACKEND_URL || ENV_BACKEND_URL === '') {
  BACKEND_URL = window.location.origin; 
} else {
  BACKEND_URL = ENV_BACKEND_URL;
}
if (BACKEND_URL.endsWith('/api')) {
  BACKEND_URL = BACKEND_URL.slice(0, -4);
}
const API = `${BACKEND_URL}/api`;

export default function GDPRDashboard() {
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Search state
  const [searchParams, setSearchParams] = useState({ rut: '', email: '', phone: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [exportToken, setExportToken] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  // Retention state
  const [retentionDryRun, setRetentionDryRun] = useState(true);
  const [retentionReport, setRetentionReport] = useState(null);

  // Incident state
  const [isIncidentDialogOpen, setIsIncidentDialogOpen] = useState(false);
  const [incidentForm, setIncidentForm] = useState({
    system_affected: '',
    detected_at: new Date().toISOString().split('T')[0],
    estimated_start: new Date().toISOString().split('T')[0],
    data_categories: 'Identificación, Contacto',
    estimated_titulars_count: 0,
    origin: '',
    impact: '',
    risk_level: 'MEDIUM',
    containment_measures: ''
  });

  // Request update state
  const [requestUpdateForm, setRequestUpdateForm] = useState({
    status: '',
    actions_taken: '',
    response_content: '',
    rejection_reason: ''
  });

  useEffect(() => {
    fetchRequests();
    fetchIncidents();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gdpr/requests`);
      setRequests(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar solicitudes ARCO');
    } finally {
      setLoading(false);
    }
  };

  const fetchIncidents = async () => {
    try {
      const res = await axios.get(`${API}/gdpr/incidents`);
      setIncidents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setSearchResults([]);
    setDownloadUrl(null);
    try {
      const { rut, email, phone } = searchParams;
      if (!rut && !email && !phone) {
        toast.warning('Ingrese al menos un criterio para buscar');
        setLoading(false);
        return;
      }
      const res = await axios.get(`${API}/gdpr/search`, { params: searchParams });
      setSearchResults(res.data);
      toast.success(`Búsqueda completada: ${res.data.length} coincidencias`);
    } catch (err) {
      console.error(err);
      toast.error('Error en búsqueda consolidada');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const { rut, email, phone } = searchParams;
      const res = await axios.post(`${API}/gdpr/export`, null, {
        params: { format_type: format, rut, email, phone }
      });
      setExportToken(res.data.token);
      setDownloadUrl(`${API}/gdpr/download/${res.data.token}`);
      toast.success('Exportación generada exitosamente. Vence en 24 horas.');
    } catch (err) {
      console.error(err);
      toast.error('Error al exportar datos');
    }
  };

  const runRetention = async () => {
    setLoading(true);
    setRetentionReport(null);
    try {
      const res = await axios.post(`${API}/gdpr/retention/run`, null, {
        params: { dry_run: retentionDryRun }
      });
      setRetentionReport(res.data);
      if (retentionDryRun) {
        toast.info('Simulación ejecutada correctamente (modo Dry-Run)');
      } else {
        toast.success('Política de retención aplicada y datos depurados');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error al ejecutar la retención');
    } finally {
      setLoading(false);
    }
  };

  const openRequestDialog = (req) => {
    setSelectedRequest(req);
    setRequestUpdateForm({
      status: req.status,
      actions_taken: req.actions_taken || '',
      response_content: req.response_content || '',
      rejection_reason: req.rejection_reason || ''
    });
    setIsRequestDialogOpen(true);
  };

  const handleUpdateRequest = async () => {
    try {
      await axios.put(`${API}/gdpr/requests/${selectedRequest.id}`, requestUpdateForm);
      toast.success('Solicitud actualizada correctamente');
      setIsRequestDialogOpen(false);
      fetchRequests();
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar la solicitud');
    }
  };

  const handleCreateIncident = async () => {
    try {
      const payload = {
        ...incidentForm,
        data_categories: incidentForm.data_categories.split(',').map(s => s.trim()),
        estimated_titulars_count: parseInt(incidentForm.estimated_titulars_count),
        tenants_affected: ["default_tenant"]
      };
      await axios.post(`${API}/gdpr/incidents`, payload);
      toast.success('Incidente registrado con éxito');
      setIsIncidentDialogOpen(false);
      fetchIncidents();
    } catch (err) {
      console.error(err);
      toast.error('Error al registrar incidente');
    }
  };

  // Helper to render due date severity colors
  const getDueDateBadge = (dueDateStr) => {
    const due = new Date(dueDateStr);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return <span className="bg-destructive/10 text-destructive border border-destructive/20 text-[11px] px-2 py-0.5 rounded font-bold">VENCIDO</span>;
    if (diffDays <= 2) return <span className="bg-destructive/10 text-destructive border border-destructive/20 text-[11px] px-2 py-0.5 rounded font-semibold">Crítico ({diffDays}d)</span>;
    if (diffDays <= 5) return <span className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-500/20 text-[11px] px-2 py-0.5 rounded">Urgente ({diffDays}d)</span>;
    if (diffDays <= 10) return <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300 border border-yellow-500/20 text-[11px] px-2 py-0.5 rounded">Advertencia ({diffDays}d)</span>;
    return <span className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-500/20 text-[11px] px-2 py-0.5 rounded">{diffDays} días</span>;
  };

  return (
    <div className="space-y-6">
      {/* Title Header matches UsersPage style */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-8 w-8 text-primary" />
            Privacidad de Datos
          </h1>
          <p className="text-muted-foreground">
            Centro de Control de Cumplimiento Técnico Ley Chilena N.º 21.719 (ARCO, Retención, Consentimiento y Brechas).
          </p>
        </div>
      </div>

      {/* Standardized tabs navigation */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'requests'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Solicitudes ARCO
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'search'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Búsqueda Consolidada
        </button>
        <button
          onClick={() => setActiveTab('retention')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'retention'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Retención de Datos
        </button>
        <button
          onClick={() => setActiveTab('incidents')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'incidents'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Incidentes de Seguridad
        </button>
      </div>

      {/* ================= TAB: REQUESTS ================= */}
      {activeTab === 'requests' && (
        <Card>
          <CardHeader>
            <CardTitle>Gestión de Solicitudes de Titulares</CardTitle>
            <CardDescription>Visualiza y resuelve las peticiones sobre derechos de información vigentes.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titular</TableHead>
                    <TableHead>Tipo de Derecho</TableHead>
                    <TableHead>Fecha de Recepción</TableHead>
                    <TableHead>Fecha Límite</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                        No hay solicitudes registradas en la empresa.
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">
                          <div>{req.titular_name}</div>
                          <div className="text-[11px] text-muted-foreground">{req.titular_rut || req.titular_email}</div>
                        </TableCell>
                        <TableCell>
                          <span className="bg-secondary text-secondary-foreground text-[11px] px-2 py-0.5 rounded font-mono">
                            {req.request_type}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(req.received_at).toLocaleDateString('es-CL')}
                        </TableCell>
                        <TableCell>
                          {getDueDateBadge(req.due_date)}
                        </TableCell>
                        <TableCell>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${
                            req.status === 'RESPONDIDA' ? 'bg-emerald-100 text-emerald-850 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-500/20' :
                            req.status === 'RECIBIDA' ? 'bg-secondary text-secondary-foreground border-border' :
                            'bg-amber-100 text-amber-850 dark:bg-amber-950 dark:text-amber-300 border-amber-500/20'
                          }`}>
                            {req.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="secondary" 
                            size="sm"
                            onClick={() => openRequestDialog(req)}
                          >
                            Gestionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ================= TAB: SEARCH ================= */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Búsqueda Consolidada de Privacidad</CardTitle>
              <CardDescription>Busca y extrae toda la información guardada sobre una persona en el tenant.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="search-rut">RUT del Titular</Label>
                  <Input 
                    id="search-rut" 
                    placeholder="12.345.678-9" 
                    value={searchParams.rut} 
                    onChange={e => setSearchParams({...searchParams, rut: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="search-email">Correo Electrónico</Label>
                  <Input 
                    id="search-email" 
                    placeholder="ejemplo@correo.com" 
                    value={searchParams.email} 
                    onChange={e => setSearchParams({...searchParams, email: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="search-phone">Teléfono</Label>
                  <Input 
                    id="search-phone" 
                    placeholder="+56912345678" 
                    value={searchParams.phone} 
                    onChange={e => setSearchParams({...searchParams, phone: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSearch} disabled={loading} className="gap-2">
                  <Search className="h-4 w-4" />
                  Buscar Registros
                </Button>
              </div>
            </CardContent>
          </Card>

          {searchResults.length > 0 && (
            <Card>
              <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <CardTitle>Resultados de la Búsqueda</CardTitle>
                  <CardDescription>Se encontraron {searchResults.length} registros conteniendo datos.</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExport('json')} className="gap-2">
                    <FileText className="h-4 w-4" />
                    Exportar JSON
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExport('csv')} className="gap-2">
                    <FileText className="h-4 w-4" />
                    Exportar CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {downloadUrl && (
                  <div className="bg-accent border border-border p-4 rounded flex justify-between items-center">
                    <div>
                      <span className="text-primary font-bold block text-sm">Exportación lista para descargar</span>
                      <span className="text-muted-foreground text-xs font-mono">{downloadUrl}</span>
                    </div>
                    <Button variant="secondary" onClick={() => window.open(downloadUrl, '_blank')} className="gap-2">
                      <Download className="h-4 w-4" />
                      Descargar Archivo
                    </Button>
                  </div>
                )}

                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {searchResults.map((item, idx) => (
                    <div key={idx} className="border border-border p-4 rounded-lg flex flex-col md:flex-row justify-between md:items-center gap-4 bg-card">
                      <div>
                        <span className="text-card-foreground font-semibold block">{item.display}</span>
                        <div className="text-[11px] text-muted-foreground mt-1 flex gap-4">
                          <span>Finalidad: <strong className="text-foreground">{item.purpose}</strong></span>
                          <span>Retención: <strong className="text-foreground">{item.retention_period}</strong></span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${
                          item.can_delete ? 'bg-emerald-100 text-emerald-850 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-500/20' : 'bg-destructive/10 text-destructive border-destructive/20'
                        }`}>
                          {item.can_delete ? 'Supresión Física Habilitada' : 'Supresión Restringida Legalmente'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ================= TAB: RETENTION ================= */}
      {activeTab === 'retention' && (
        <Card>
          <CardHeader>
            <CardTitle>Políticas y Depuración Automática de Datos</CardTitle>
            <CardDescription>Ejecuta la depuración controlada de datos con finalidades vencidas.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between bg-accent border border-border p-6 rounded-lg">
              <div className="space-y-2">
                <Label className="text-base font-semibold">Configuración del Job</Label>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="dry-run-checkbox" 
                    checked={retentionDryRun} 
                    onChange={e => setRetentionDryRun(e.target.checked)}
                    className="rounded border-border text-primary h-4 w-4 bg-background"
                  />
                  <Label htmlFor="dry-run-checkbox" className="text-foreground">
                    Modo Simulado (Dry-Run) activo por defecto
                  </Label>
                </div>
                <p className="text-muted-foreground text-xs">
                  En modo simulado, el sistema solo reportará cuántos chats, logs y registros expiran sin eliminarlos de la base de datos real.
                </p>
              </div>
              <Button onClick={runRetention} disabled={loading} className="gap-2">
                <Play className="h-4 w-4" />
                Ejecutar Retención
              </Button>
            </div>

            {retentionReport && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                  Reporte de la Depuración de Datos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="border border-border p-4 rounded-lg bg-card">
                    <span className="text-muted-foreground text-xs block">Chats IA Eliminados</span>
                    <span className="text-2xl font-bold text-foreground">{retentionReport.agent_chats_deleted}</span>
                  </div>
                  <div className="border border-border p-4 rounded-lg bg-card">
                    <span className="text-muted-foreground text-xs block">Logs de Actividad Eliminados</span>
                    <span className="text-2xl font-bold text-foreground">{retentionReport.user_activity_deleted}</span>
                  </div>
                  <div className="border border-border p-4 rounded-lg bg-card">
                    <span className="text-muted-foreground text-xs block">Clientes Anonimizados</span>
                    <span className="text-2xl font-bold text-foreground">{retentionReport.customers_anonymized}</span>
                  </div>
                  <div className="border border-border p-4 rounded-lg bg-card">
                    <span className="text-muted-foreground text-xs block">Proveedores Anonimizados</span>
                    <span className="text-2xl font-bold text-foreground">{retentionReport.suppliers_anonymized}</span>
                  </div>
                </div>

                <div className="border border-border bg-muted rounded p-4 max-h-[250px] overflow-y-auto font-mono text-xs text-muted-foreground space-y-1">
                  {retentionReport.details.length === 0 ? (
                    <div>No se identificaron registros vencidos en este ciclo.</div>
                  ) : (
                    retentionReport.details.map((detail, idx) => (
                      <div key={idx}>{detail}</div>
                    ))
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ================= TAB: INCIDENTS ================= */}
      {activeTab === 'incidents' && (
        <Card>
          <CardHeader className="flex flex-row justify-between items-center">
            <div>
              <CardTitle>Bitácora de Incidentes de Seguridad</CardTitle>
              <CardDescription>Registro obligatorio e información de brechas o vulnerabilidades.</CardDescription>
            </div>
            <Button onClick={() => setIsIncidentDialogOpen(true)}>Registrar Brecha</Button>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sistema Afectado</TableHead>
                    <TableHead>Riesgo / Impacto</TableHead>
                    <TableHead>Detección</TableHead>
                    <TableHead>Titulares Afectados</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                        No hay incidentes de seguridad registrados en el sistema.
                      </TableCell>
                    </TableRow>
                  ) : (
                    incidents.map((inc) => (
                      <TableRow key={inc.id}>
                        <TableCell className="font-semibold text-foreground">
                          {inc.system_affected}
                        </TableCell>
                        <TableCell>
                          <span className={`text-[10px] px-2 py-0.5 rounded font-mono border ${
                            inc.risk_level === 'CRITICAL' || inc.risk_level === 'HIGH' ? 'bg-destructive/10 text-destructive border-destructive/20' : 'bg-amber-100 text-amber-800 border-amber-500/20'
                          }`}>
                            {inc.risk_level}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(inc.detected_at).toLocaleDateString('es-CL')}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          ~{inc.estimated_titulars_count} personas
                        </TableCell>
                        <TableCell>
                          <span className="bg-secondary text-secondary-foreground border border-border text-[11px] px-2 py-0.5 rounded-full">
                            {inc.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ================= DIALOG: REQUEST MANAGEMENT ================= */}
      {isRequestDialogOpen && selectedRequest && (
        <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">Gestionar Solicitud de Titular</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm bg-muted p-4 rounded border border-border">
                <div>
                  <span className="text-muted-foreground text-xs block">Titular</span>
                  <span className="font-medium text-foreground">{selectedRequest.titular_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs block">Derecho</span>
                  <span className="font-mono text-primary">{selectedRequest.request_type}</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="req-status">Estado de la Solicitud</Label>
                <Select 
                  value={requestUpdateForm.status} 
                  onValueChange={val => setRequestUpdateForm({...requestUpdateForm, status: val})}
                >
                  <SelectTrigger id="req-status">
                    <SelectValue placeholder="Seleccione Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RECIBIDA">RECIBIDA</SelectItem>
                    <SelectItem value="EN_VALIDACION">EN_VALIDACION</SelectItem>
                    <SelectItem value="BLOQUEADA_TEMPORALMENTE">BLOQUEADA_TEMPORALMENTE</SelectItem>
                    <SelectItem value="EN_PROCESO">EN_PROCESO</SelectItem>
                    <SelectItem value="RESPONDIDA">RESPONDIDA</SelectItem>
                    <SelectItem value="RECHAZADA">RECHAZADA</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="req-actions">Acciones Realizadas</Label>
                <Input 
                  id="req-actions" 
                  placeholder="Ej: Seudonimización de datos en chats de IA"
                  value={requestUpdateForm.actions_taken} 
                  onChange={e => setRequestUpdateForm({...requestUpdateForm, actions_taken: e.target.value})}
                />
              </div>

              {requestUpdateForm.status === 'RESPONDIDA' && (
                <div className="space-y-1.5">
                  <Label htmlFor="req-response">Respuesta Entregada</Label>
                  <Input 
                    id="req-response" 
                    placeholder="Texto enviado al titular"
                    value={requestUpdateForm.response_content} 
                    onChange={e => setRequestUpdateForm({...requestUpdateForm, response_content: e.target.value})}
                  />
                </div>
              )}

              {requestUpdateForm.status === 'RECHAZADA' && (
                <div className="space-y-1.5">
                  <Label htmlFor="req-rejection">Motivo de Rechazo</Label>
                  <Input 
                    id="req-rejection" 
                    placeholder="Justificación legal (ej: Plazo legal tributario de 6 años)"
                    value={requestUpdateForm.rejection_reason} 
                    onChange={e => setRequestUpdateForm({...requestUpdateForm, rejection_reason: e.target.value})}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRequestDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleUpdateRequest}>Guardar Cambios</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ================= DIALOG: BREACH REGISTRATION ================= */}
      {isIncidentDialogOpen && (
        <Dialog open={isIncidentDialogOpen} onOpenChange={setIsIncidentDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">Registrar Brecha de Seguridad</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto pr-2">
              <div className="space-y-1.5">
                <Label htmlFor="inc-system">Sistema Afectado</Label>
                <Input 
                  id="inc-system" 
                  placeholder="Ej: Base de datos MongoDB POS"
                  value={incidentForm.system_affected} 
                  onChange={e => setIncidentForm({...incidentForm, system_affected: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="inc-detected">Fecha Detección</Label>
                  <Input 
                    id="inc-detected" 
                    type="date"
                    value={incidentForm.detected_at} 
                    onChange={e => setIncidentForm({...incidentForm, detected_at: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inc-started">Fecha Inicio Estimada</Label>
                  <Input 
                    id="inc-started" 
                    type="date"
                    value={incidentForm.estimated_start} 
                    onChange={e => setIncidentForm({...incidentForm, estimated_start: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="inc-categories">Categoría de Datos (Separados por coma)</Label>
                <Input 
                  id="inc-categories" 
                  value={incidentForm.data_categories} 
                  onChange={e => setIncidentForm({...incidentForm, data_categories: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="inc-count">Titulares Estimados</Label>
                  <Input 
                    id="inc-count" 
                    type="number"
                    value={incidentForm.estimated_titulars_count} 
                    onChange={e => setIncidentForm({...incidentForm, estimated_titulars_count: e.target.value})}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inc-risk">Rival de Riesgo</Label>
                  <Select 
                    value={incidentForm.risk_level} 
                    onValueChange={val => setIncidentForm({...incidentForm, risk_level: val})}
                  >
                    <SelectTrigger id="inc-risk">
                      <SelectValue placeholder="Seleccione Riesgo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">BAJO</SelectItem>
                      <SelectItem value="MEDIUM">MEDIO</SelectItem>
                      <SelectItem value="HIGH">ALTO</SelectItem>
                      <SelectItem value="CRITICAL">CRÍTICO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="inc-origin">Origen de la Brecha</Label>
                <Input 
                  id="inc-origin" 
                  placeholder="Ej: Error humano en configuración de puertos"
                  value={incidentForm.origin} 
                  onChange={e => setIncidentForm({...incidentForm, origin: e.target.value})}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="inc-measures">Medidas de Contención Inmediatas</Label>
                <Input 
                  id="inc-measures" 
                  placeholder="Ej: Cierre de puertos expuestos y revocación de llaves API"
                  value={incidentForm.containment_measures} 
                  onChange={e => setIncidentForm({...incidentForm, containment_measures: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsIncidentDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateIncident}>Registrar Incidente</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
